import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useConfig } from '@entities/config/api';
import { useGuestSession, useStartGuestSession } from '@entities/guest';
import { useMe } from '@entities/user/api';
import { Button, Container, Skeleton, StateBlock } from '@shared/ui';

import { useAuthDialog } from '../model/authDialogContext';
import type { PendingActionType } from '../model/pendingAction';

import styles from './RequireAuth.module.css';

interface RequireOwnerProps {
  children: ReactNode;
  pendingType?: PendingActionType | undefined;
}

/**
 * Guard экранов, доступных **и гостю** (FE-205, MVP_PRD §41.1).
 *
 * Шаги 1–2 воронки — диалог с Karl, критерии и анализ — проходятся без
 * регистрации: требовать её до того, как человек увидел хоть что-то полезное,
 * — самый дорогой барьер в воронке. Поэтому владельцем заказа здесь может быть
 * и гостевая сессия.
 *
 * Порядок решений:
 *
 * 1. есть пользователь — показываем экран;
 * 2. есть гостевая сессия — показываем экран;
 * 3. гостевой контур включён — заводим сессию молча и показываем экран:
 *    отдельная кнопка «продолжить как гость» ничего не добавляет, а лишний шаг
 *    стоит воронке;
 * 4. контур выключен — обычное окно входа.
 *
 * **Гард не является защитой.** Ownership и авторизацию проверяет backend на
 * каждом запросе; здесь решается только то, что показать вместо экрана.
 */
export function RequireOwner({ children, pendingType = 'navigate' }: RequireOwnerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { requestAuth } = useAuthDialog();

  const me = useMe();
  const config = useConfig();
  const guestEnabled = config.data?.guest.enabled ?? false;

  const authenticated = me.data != null;
  // Гостевая сессия запрашивается только тогда, когда пользователя нет: у
  // вошедшего она не спрашивается вовсе, и лишнего запроса на каждый экран нет.
  const guest = useGuestSession(!me.isPending && !authenticated);
  const start = useStartGuestSession();

  const prompted = useRef(false);
  const started = useRef(false);
  const returnUrl = `${location.pathname}${location.search}`;

  const needsGuest =
    !me.isPending && !authenticated && guest.isSuccess && guest.data === null && guestEnabled;

  useEffect(() => {
    if (!needsGuest || started.current) return;
    started.current = true;
    start.mutate();
  }, [needsGuest, start]);

  const needsAuth =
    !me.isPending &&
    !authenticated &&
    guest.isSuccess &&
    guest.data === null &&
    config.isSuccess &&
    !guestEnabled;

  useEffect(() => {
    if (!needsAuth || prompted.current) return;
    prompted.current = true;
    requestAuth({ type: pendingType, returnUrl });
  }, [needsAuth, pendingType, requestAuth, returnUrl]);

  if (authenticated || guest.data != null) return <>{children}</>;

  if (me.isError || guest.isError || start.isError) {
    return (
      <Container>
        <div className={styles.state}>
          <StateBlock
            tone="error"
            title={t('error.title')}
            description={t('error.description')}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  void me.refetch();
                  void guest.refetch();
                }}
              >
                {t('error.retry')}
              </Button>
            }
          />
        </div>
      </Container>
    );
  }

  if (needsAuth) {
    return (
      <Container>
        <div className={styles.state}>
          <StateBlock
            title={t('auth.guard.title')}
            description={t('auth.guard.text')}
            action={
              <Button
                onClick={() => {
                  requestAuth({ type: pendingType, returnUrl });
                }}
              >
                {t('nav.login')}
              </Button>
            }
          />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className={styles.loading}>
        <Skeleton height="2rem" width="40%" />
        <Skeleton height="12rem" radius="var(--radius-card)" />
      </div>
    </Container>
  );
}
